import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Button, Spinner, useToast } from './ui';
import { useCart } from '../context/CartContext';
import ProductImage from './ProductImage';
import useApprovalStatus from '../hooks/useApprovalStatus';
import './QuickView.css';

/**
 * Quick-view modal for product cards. Fetches /api/products/:id and
 * renders a compressed product detail with variant chooser + add-to-cart
 * — without leaving the listing page.
 *
 *   const [qvId, setQvId] = useState(null);
 *   <QuickView productId={qvId} onClose={() => setQvId(null)} />
 */
export default function QuickView({ productId, onClose }) {
  const [product, setProduct] = useState(null);
  const [variantId, setVariantId] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const { addToCart } = useCart();
  const toast = useToast();
  const { isApproved, isLoggedIn } = useApprovalStatus();

  useEffect(() => {
    if (!productId) { setProduct(null); return; }
    setLoading(true);
    fetch(`/api/products/${productId}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        setProduct(d);
        if (d?.variants?.length) {
          const inStock = d.variants.find(v => v.stock > 0) || d.variants[0];
          setVariantId(inStock.id);
        }
        setQty(1);
      })
      .finally(() => setLoading(false));
  }, [productId]);

  const variant = product?.variants?.find(v => v.id === variantId) || null;
  const effectivePrice = product
    ? (product.price || 0) + (variant?.price_adjustment || 0)
    : 0;
  const effectiveStock = variant ? variant.stock : product?.stock ?? 0;
  const onSale = product && product.compare_at_price && product.compare_at_price > effectivePrice;

  function add() {
    if (!product) return;
    const eff = variant
      ? { ...product, variant_id: variant.id, condition: variant.variant_value, price: effectivePrice, stock: variant.stock }
      : product;
    addToCart(eff, qty);
    toast.success(`${qty} × ${product.name}${variant ? ` (${variant.variant_value})` : ''} added to cart`);
    onClose?.();
  }

  return (
    <Modal open={!!productId} onClose={onClose} size="lg" title={product?.name || ''}>
      {loading && <Spinner label="Loading…" />}
      {!loading && !product && <p>Product unavailable.</p>}
      {product && (
        <div className="qv">
          <div className="qv__image">
            <ProductImage product={product} alt={product.name} />
          </div>
          <div className="qv__body">
            <div className="qv__meta">
              {product.set_name && <span>{product.set_name}</span>}
              {product.card_number && <span>· #{product.card_number}</span>}
              {product.rarity && <span>· {product.rarity}</span>}
            </div>

            {isApproved ? (
              <div className="qv__price">
                {onSale && <span className="qv__compare">${Number(product.compare_at_price).toFixed(2)}</span>}
                <span className={onSale ? 'qv__price--sale' : ''}>
                  ${Number(effectivePrice).toFixed(2)}
                </span>
                {onSale && (
                  <span className="qv__discount">
                    {Math.round((1 - effectivePrice / product.compare_at_price) * 100)}% off
                  </span>
                )}
              </div>
            ) : (
              <div className="qv__price" aria-hidden="true" style={{ filter: 'blur(4px)', userSelect: 'none', pointerEvents: 'none' }}>
                <span>$••.••</span>
              </div>
            )}

            <div className="qv__stock">
              {effectiveStock <= 0
                ? <span className="qv__oos">Out of stock</span>
                : effectiveStock < 5
                  ? <span className="qv__low">Only {effectiveStock} left!</span>
                  : <span className="qv__in">{effectiveStock >= 10 ? '10+' : effectiveStock} in stock</span>}
            </div>

            {product.variants && product.variants.length > 0 && (
              <div className="qv__variants">
                <div className="qv__variants-label">Condition</div>
                <div className="qv__variants-row">
                  {product.variants.map(v => (
                    <button
                      key={v.id}
                      type="button"
                      className={`qv__variant${v.id === variantId ? ' qv__variant--active' : ''}`}
                      disabled={v.stock <= 0}
                      onClick={() => setVariantId(v.id)}
                    >
                      <div>{v.variant_value}</div>
                      {isApproved
                        ? <small>${((product.price || 0) + (v.price_adjustment || 0)).toFixed(2)}</small>
                        : <small style={{ filter: 'blur(3px)' }} aria-hidden="true">$••.••</small>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {effectiveStock > 0 && (
              <div className="qv__qty">
                <label htmlFor="qv-qty">Quantity</label>
                <div className="qv__qty-row">
                  <button type="button" onClick={() => setQty(q => Math.max(1, q - 1))}>−</button>
                  <input id="qv-qty" type="number" min="1" max={effectiveStock} value={qty}
                    onChange={e => setQty(Math.max(1, Math.min(effectiveStock, Number(e.target.value) || 1)))} />
                  <button type="button" onClick={() => setQty(q => Math.min(effectiveStock, q + 1))}>+</button>
                </div>
              </div>
            )}

            <Modal.Actions>
              <Button as={Link} to={`/product/${product.id}`} variant="secondary" onClick={onClose}>
                View full details
              </Button>
              {isApproved ? (
                <Button onClick={add} disabled={effectiveStock <= 0}>
                  Add to cart
                </Button>
              ) : (
                <Button as={Link}
                  to={isLoggedIn ? '/apply' : `/login?next=${encodeURIComponent(window.location.pathname)}`}
                  onClick={onClose}>
                  {isLoggedIn ? 'Apply for access' : 'Sign in to view price'}
                </Button>
              )}
            </Modal.Actions>
          </div>
        </div>
      )}
    </Modal>
  );
}
